import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Icon, MultiSelect, SelectOption } from '../../../../components';
import { useClickOutside, useSimActions } from '../../../../hooks';
import { useGetTeamsQuery, useGetUsersForSelectQuery } from '../../../../services';
import { ComponentSize, ProspectAllowedCaller, RoleFilterType, Roles, Team } from '../../../../types';
import { areStrArraysEqual, getTagColorFromHex } from '../../../../utils';
import HoverableContent from './HoverableContent';

const BADGE_MAX_WIDTH = 150;

interface SimulationDetailsSharedWithProps {
  personaId: string;
  currAllowedCallers?: ProspectAllowedCaller[];
  currAllowedTeams?: Team[];
}

const SimulationDetailsSharedWith = ({
  personaId,
  currAllowedCallers,
  currAllowedTeams,
}: SimulationDetailsSharedWithProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const [inputCallerIds, setInputCallerIds] = useState<string[]>([]);
  const [inputTeamIds, setInputTeamIds] = useState<string[]>([]);

  // Ref for tracking the simulation ID when editing starts.
  // Used to ensure tags are applied to the correct simulation even if it changes during editing,
  // (ie. opening another simulation with changes applied to the current simulation).
  const personaIdToEditRef = useRef<string>(personaId);
  // Ref for handling click outside events.
  const inputRef = useRef<HTMLDivElement>(null);

  const { runEdit } = useSimActions();

  const { data: orgUsers = { users: [] } } = useGetUsersForSelectQuery({
    roleFilterType: RoleFilterType.SPECIFY_ROLES,
    roles: [Roles.SALES_REP],
  });

  const { data: orgTeams = [] } = useGetTeamsQuery();

  const callerOptions = orgUsers.users.map((user) => ({
    label: user.name ?? user.email ?? '', // Use name if available, fallback to email
    value: user.id,
  }));

  const teamOptions = orgTeams.map((team) => ({
    label: team.name,
    value: team.id,
    color: team.color,
  }));

  // Combine selected callers and teams into a single array of SelectOption objects.
  const inputSharedWith: SelectOption[] = [
    ...callerOptions.filter(({ value }) => inputCallerIds.includes(value)),
    ...teamOptions.filter(({ value }) => inputTeamIds.includes(value)),
  ];

  // Create a flat array of all selected IDs (both callers and teams).
  const inputSharedWithIds = [...inputCallerIds, ...inputTeamIds];

  const currCallerIds = useMemo(() => (currAllowedCallers || []).map(({ id }) => id), [currAllowedCallers]);
  const currTeamIds = useMemo(() => (currAllowedTeams || []).map(({ id }) => id), [currAllowedTeams]);

  // Update the ref with the current personaId when editing starts.
  const onEdit = useCallback(() => {
    personaIdToEditRef.current = personaId;
    setIsEditing(true);
  }, [personaId]);

  const onChange = useCallback(
    (selected?: string[]) => {
      const newCallerIds = callerOptions.filter(({ value }) => selected?.includes(value));
      const newTeamIds = teamOptions.filter(({ value }) => selected?.includes(value));
      setInputCallerIds(newCallerIds.map(({ value }) => value));
      setInputTeamIds(newTeamIds.map(({ value }) => value));
    },
    [callerOptions, teamOptions]
  );

  const onSubmit = useCallback(() => {
    // If selected representatives/teams did not change, do nothing.
    if (areStrArraysEqual(inputCallerIds, currCallerIds) && areStrArraysEqual(inputTeamIds, currTeamIds)) {
      setIsEditing(false);
      return;
    }

    runEdit({
      payload: {
        id: personaIdToEditRef.current,
        allowedCallers: inputCallerIds,
        allowedTeams: inputTeamIds,
      },
      onError: () => {
        setInputCallerIds(currCallerIds);
        setInputTeamIds(currTeamIds);
      }, // Reset the selected representatives/teams on error.
    }).then(() => {
      setIsEditing(false);
    }); // Close edit mode regardless of submission success.
  }, [currCallerIds, currTeamIds, inputCallerIds, inputTeamIds]);

  // Reset state when persona or current tags change.
  useEffect(() => {
    setIsEditing(false);
    setInputCallerIds(currCallerIds);
    setInputTeamIds(currTeamIds);
  }, [personaId, currCallerIds, currTeamIds]);

  // Handle click outside to submit changes.
  useClickOutside(inputRef, onSubmit);

  return isEditing ? (
    <MultiSelect
      ref={inputRef}
      options={[...callerOptions, ...teamOptions]}
      selected={inputSharedWithIds}
      size={ComponentSize.X_SMALL}
      onChange={onChange}
      placeholder="Select representatives or teams"
      onClose={onSubmit}
      wrap
    />
  ) : (
    <HoverableContent
      icon={Icon.EDIT}
      content={
        inputSharedWith.length ? (
          <div className="flex flex-wrap justify-end gap-2">
            {inputSharedWith.map((member) => (
              <Badge
                key={member.value}
                color={getTagColorFromHex(member.color)}
                label={member.label}
                maxWidth={BADGE_MAX_WIDTH}
              />
            ))}
          </div>
        ) : (
          'Managers & admins'
        )
      }
      onClick={onEdit}
      tooltip="Edit tags"
    />
  );
};

export default SimulationDetailsSharedWith;
